import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	input,
	output,
	signal,
	viewChild,
} from '@angular/core';
import { DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCell, MatTableModule } from '@angular/material/table';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatOption, MatSelect } from '@angular/material/select';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { DataBagService } from '@/common/data-bag.service';
import { filter } from 'rxjs';
import { BagTransportTermApiService } from '@/bag/bag-transport-term/bag-transport-term-api.service';
import { BagTransportTerm, TransportTermList } from '@/bag/bag-transport-term/bag-transport-term.model';
import { BagTransportTermDialogComponent } from '@/bag/bag-transport-term/bag-transport-term-dialog/bag-transport-term-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { CountValueTransportTerm } from '@/common/pipes/count-value-transport-term.pipe';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { camelToSnakeCase } from '@/_utils/string/camel-to-snake-case';
import { TransportTermMatDataService } from '../transport-term-mat-data.service';
import { getTransportTermColor } from '../process-bag-transport-term-data';
import { TransportAssignDialogComponent } from '../transport-assign-dialog/transport-assign-dialog.component';
import { TermFilesDialogComponent } from '../term-files-dialog/term-files-dialog.component';
import { TransportTerm } from '@/new-transport/_models/transport.model';
import { MatMenuModule } from '@angular/material/menu';
import { CheckListDialogComponent } from '../check-list-dialog/check-list-dialog.component';
import { Router, UrlTree } from '@angular/router';

const DEFAULT_ORDERING = 'accommodation__startswith';

@Component({
	selector: 'app-bag-transport-term-table',
	standalone: true,
	imports: [
		DatePipe,
		MatButtonModule,
		MatCell,
		MatTableModule,
		MatCheckbox,
		FormsModule,
		MatTooltip,
		MatSlideToggle,
		MatPaginator,
		NgIf,
		MatSelect,
		MatOption,
		NgForOf,
		MatSortModule,
		NgClass,
		CountValueTransportTerm,
		MatProgressSpinnerModule,
		MatMenuModule,
	],
	templateUrl: './bag-transport-term-table.component.html',
	styleUrl: './bag-transport-term-table.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BagTransportTermTableComponent implements AfterViewInit {
	parameters = input<any>();
	readonly allItemsSize = input(0);
	readonly isLoading = input(false);
	readonly page = input(0);

	readonly OfferTransportKind = OfferTransportKind;

	readonly pageChange = output<PageEvent>();
	readonly sortedByColumn = output<string>();
	readonly deadlineChanged = output<void>();
	readonly responsibleUserHasChanged = output<boolean>();

	readonly displayedColumns = [
		'checkbox',
		'id',
		'name',
		'kind',
		'departureTime',
		'accommodationLower',
		'accommodationUpper',
		'returnTime',
		'sumOfTransportDeparture',
		'sumOfTransportReturn',
		'accomodation',
		'status',
		'organizationStatus',
		'touristGuides',
		'highlightLastSeats',
		'confirmTime',
		'swinging',
		'responsibleUser',
		'earliestDeadline',
		'stopSale',
		'menu',
	];

	readonly matDataService = inject(TransportTermMatDataService);

	private readonly lastClickedIndex = signal<number>(null);
	private readonly actionTerm = signal(true);
	private readonly stopSale = signal(false);

	private readonly sort = viewChild(MatSort);

	private readonly infobox = inject(InfoboxService);
	private readonly bag = inject(DataBagService);
	private readonly bagTransportTermApiService = inject(BagTransportTermApiService);
	private readonly dialog = inject(MatDialog);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly router = inject(Router);

	ngAfterViewInit(): void {
		this.matDataService.matData.sort = this.sort();
		this.customSorting();
	}

	checkMultipleSelection(event: MouseEvent, index: number): void {
		if (event.shiftKey) {
			if (this.lastClickedIndex() === null) {
				return;
			}
			event.preventDefault();
			const array = this.matDataService.matData.sortData(
				this.matDataService.matData.data,
				this.matDataService.matData.sort,
			);
			this.range(this.lastClickedIndex(), index).forEach((iter: number) => {
				array[iter].checked = true;
			});
		}
		this.lastClickedIndex.set(index);
	}

	changeStatus(element: TransportTermList): void {
		this.actionTerm.set(true);
		this.changeStatusApi(element, this.actionTerm());
	}

	changeOrganizationStatus(element: TransportTermList, event: number): void {
		this.bagTransportTermApiService
			.patchTransportTerm(element.id, {
				organizationStatus: (event as unknown as TransportTerm['organizationStatus']) ?? null,
			})
			.subscribe((result) => {
				element.organizationStatus = result.organizationStatus;
				this.cdr.detectChanges();
			});
	}

	openDialog(transportTerm: BagTransportTerm): void {
		const dialogRef = this.dialog
			.open(BagTransportTermDialogComponent, {
				minWidth: '800px',
				maxWidth: '1000px',
				data: transportTerm,
				maxHeight: '90vh',
			})
			.afterClosed()
			.subscribe(() => {
				dialogRef.unsubscribe();
			});
	}

	patchHighlightLastSeats(element: TransportTerm): void {
		this.patchTransportTerm(element.id, { highlightLastSeats: element.highlightLastSeats });
	}

	patchStopSale(element: TransportTerm): void {
		this.patchTransportTerm(element.id, { stopSale: element.stopSale });
	}

	pageChanged(event: PageEvent): void {
		this.pageChange.emit(event);
	}

	onSortChange({ active, direction }: Sort): void {
		if (!direction) {
			this.sortedByColumn.emit(DEFAULT_ORDERING);
			return;
		}

		const sortField = this.mapSortField(active);
		const sortPrefix = direction === 'asc' ? '' : '-';

		this.sortedByColumn.emit(`${sortPrefix}${sortField}`);
	}

	openFilesDialog(transportTerm: BagTransportTerm): void {
		this.dialog.open(TermFilesDialogComponent, {
			width: '1000px',
			data: transportTerm.id,
		});
	}

	openCheckListDialog(transportTerm: BagTransportTerm): void {
		const dialogRef = this.dialog.open(CheckListDialogComponent, {
			width: '1100px',
			data: transportTerm.id,
			maxHeight: '85vh',
		});
		dialogRef.beforeClosed().subscribe(() => {
			if (dialogRef.componentInstance?.deadlineShouldBeReloaded()) {
				this.deadlineChanged.emit();
			}
		});
	}

	openAssignDialog(transportTerm: BagTransportTerm): void {
		this.dialog
			.open<TransportAssignDialogComponent, Pick<BagTransportTerm, 'id' | 'responsibleUser'>>(
				TransportAssignDialogComponent,
				{
					width: '1000px',
					data: { id: transportTerm.id, responsibleUser: transportTerm.responsibleUser },
				},
			)
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe((result) => {
				this.responsibleUserHasChanged.emit(result);
			});
	}

	jumpToProduct(term: BagTransportTerm): void {
		const tree = term.swinging ? this.buildLocationUrl(term) : this.buildProductUrl(term);

		if (!tree) {
			console.error('Could not build product URL', term);
			return;
		}
		const url = this.router.serializeUrl(tree);

		window.open(url, '_blank');
	}

	private buildLocationUrl(term: BagTransportTerm): UrlTree | null {
		const locationId = term.transportStop?.location;
		if (!locationId) {
			return null;
		}
		return this.router.createUrlTree(['/product'], {
			queryParams: { location__id__in: locationId },
		});
	}

	private buildProductUrl(term: BagTransportTerm): UrlTree | null {
		const productId = term.products?.[0];
		if (!productId) {
			return null;
		}
		return this.router.createUrlTree(['/product', term.products?.[0]]);
	}

	private changeStatusApi(element: TransportTermList, action_term: boolean): void {
		this.stopSale.set(element.status === 0);

		this.bag.patch(
			'/transport/transport_term/' + element.id + '/',
			{
				status: element.status,
				action_term: action_term,
				stop_sale: this.stopSale(),
			},
			(item, error) => {
				if (error) {
					return;
				}
			},
		);
		element.color = getTransportTermColor(element.status);
	}

	private customSorting(): void {
		this.matDataService.matData.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'accommodationLower':
					return item.accommodation?.lower;
				case 'accommodationUpper':
					return item.accommodation?.upper;
				default:
					return item[property];
			}
		};
	}

	private range(first: number, sec: number): number[] {
		const start = Math.min(first, sec);
		const len = Math.abs(first - sec) + 1;
		return Array.from({ length: len }, (x, i) => i + start);
	}

	private patchTransportTerm(id: number, patchData: Partial<TransportTerm>): void {
		this.bagTransportTermApiService.patchTransportTerm(id, patchData).subscribe(() => {
			this.infobox.success('Zapisano');
		});
	}

	private mapSortField(active: string): string {
		switch (active) {
			case 'accommodationLower':
				return 'accommodation__startswith';
			case 'accommodationUpper':
				return 'accommodation__endswith';
			default:
				return camelToSnakeCase(active);
		}
	}
}
